<template>
  <div
    class="circular-slider"
    :style="{ width: size + 'px', height: size / 1.2 + 'px' }"
    @mousedown="startDrag"
    @touchstart.prevent="startDrag"
    role="slider"
    :aria-valuemin="min"
    :aria-valuemax="max"
    :aria-valuenow="internalValue"
    tabindex="0"
    @keydown.prevent="onKey"
  >
    <svg :width="size" :height="size">
      <!-- Track Arc -->
      <path class="track" :d="trackPath" />
      <!-- Progress Arc -->
      <path
        v-if="fraction > 0"
        class="progress"
        :d="progressPath"
        :style="{ stroke: progressColor }"
      />
      <!-- Thumb (cursore) -->
      <circle
        class="thumb"
        :cx="thumbX"
        :cy="thumbY"
        :r="thumbRadius"
        :style="{ stroke: thumbStrokeColor }"
      />
      <!-- Value -->
      <text
        class="value-text"
        :x="center"
        :y="center"
        dominant-baseline="middle"
        text-anchor="middle"
        :style="{ fill: textColor }"
      >
        {{ displayValue }}°
      </text>
      <text
        v-if="label"
        class="value-text"
        :x="center"
        :y="center + 40"
        dominant-baseline="middle"
        text-anchor="middle"
        style="font-size: 20px; fill: #666"
      >
        {{ label }}
      </text>
    </svg>
  </div>
</template>

<script>
export default {
  name: "CircularTemperatureSlider",
  props: {
    value: { type: Number, required: true },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 20 },
    step: { type: Number, default: 0.1 },
    size: { type: Number, default: 240 },
    thumbRadius: { type: Number, default: 10 },
    arcDegrees: { type: Number, default: 270 }, // ampiezza arco visibile
    startAngle: { type: Number, default: 225 }, // start effettivo richiesto
    strokeWidth: { type: Number, default: 18 },
    ringTolerance: { type: Number, default: 0.7 }, // percentuale di stroke usata come tolleranza
    label: { type: String, default: "" },
    monotonic: { type: Boolean, default: false },
    hitExtendPx: { type: Number, default: 20 }, // estensione extra area sensibile drag (px)
  },
  data() {
    return {
      dragging: false,
      internalValue: this.value,
    };
  },
  computed: {
    center() {
      return this.size / 2;
    },
    radius() {
      return this.size / 2 - 20;
    },
    total() {
      return this.max - this.min;
    },
    fraction() {
      return (this.internalValue - this.min) / this.total;
    },
    angle() {
      return this.startAngle + this.fraction * this.arcDegrees;
    },
    radians() {
      return ((this.angle - 90) * Math.PI) / 180;
    }, // 0° reference at top
    thumbX() {
      return this.center + this.radius * Math.cos(this.radians);
    },
    thumbY() {
      return this.center + this.radius * Math.sin(this.radians);
    },
    // SVG Arc paths
    startPoint() {
      return this.polarToCartesian(this.startAngle);
    },
    endPoint() {
      return this.polarToCartesian(this.startAngle + this.arcDegrees);
    },
    currentPoint() {
      return this.polarToCartesian(this.angle);
    },
    largeArcFlagFull() {
      return this.arcDegrees > 180 ? 1 : 0;
    },
    largeArcFlagCurrent() {
      return this.fraction * this.arcDegrees > 180 ? 1 : 0;
    },
    trackPath() {
      const sp = this.startPoint;
      const ep = this.endPoint;
      return `M ${sp.x} ${sp.y} A ${this.radius} ${this.radius} 0 ${this.largeArcFlagFull} 1 ${ep.x} ${ep.y}`;
    },
    progressPath() {
      if (this.fraction <= 0) return "";
      const sp = this.startPoint;
      const cp = this.currentPoint;
      return `M ${sp.x} ${sp.y} A ${this.radius} ${this.radius} 0 ${this.largeArcFlagCurrent} 1 ${cp.x} ${cp.y}`;
    },
    displayValue() {
      return this.internalValue.toFixed(1);
    },
    progressColor() {
      if (this.monotonic) return "#bbbbbb";
      const stops = [
        { p: 0.0, c: "#1976d2" },
        { p: 0.25, c: "#2e7d32" },
        { p: 0.5, c: "#fdd835" },
        { p: 0.75, c: "#fb8c00" },
        { p: 1.0, c: "#d32f2f" },
      ];
      const f = this.fraction <= 0 ? 0 : this.fraction >= 1 ? 1 : this.fraction;
      let a = stops[0],
        b = stops[stops.length - 1];
      for (let i = 0; i < stops.length - 1; i++) {
        if (f >= stops[i].p && f <= stops[i + 1].p) {
          a = stops[i];
          b = stops[i + 1];
          break;
        }
      }
      const span = (f - a.p) / (b.p - a.p || 1);
      const ca = this.hexToRgb(a.c);
      const cb = this.hexToRgb(b.c);
      const r = Math.round(ca.r + (cb.r - ca.r) * span);
      const g = Math.round(ca.g + (cb.g - ca.g) * span);
      const bch = Math.round(ca.b + (cb.b - ca.b) * span);
      return `rgb(${r},${g},${bch})`;
    },
    textColor() {
      return this.monotonic ? "#616161" : this.progressColor;
    },
    thumbStrokeColor() {
      return this.monotonic ? "#757575" : this.progressColor;
    },
  },
  watch: {
    value(v) {
      if (!this.dragging) this.internalValue = this.clamp(v);
    },
  },
  methods: {
    clamp(v) {
      return Math.min(this.max, Math.max(this.min, v));
    },
    quantize(v) {
      const steps = Math.round((v - this.min) / this.step);
      return this.min + steps * this.step;
    },
    emit() {
      this.$emit("input", this.internalValue);
      this.$emit("change", this.internalValue);
    },
    setFromEvent(e) {
      const rect = e.target.closest(".circular-slider").getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const x = clientX - rect.left - this.center;
      const y = clientY - rect.top - this.center;
      // Calcola angolo (0° in alto, senso orario)
      let ang = (Math.atan2(y, x) * 180) / Math.PI + 90;
      if (ang < 0) ang += 360;
      // Normalizza rispetto allo startAngle e arco disponibile
      let rel = ang - this.startAngle;
      if (rel < 0) rel += 360; // porta in range positivo
      // Se fuori dall'arco non fare nulla
      if (rel < 0 || rel > this.arcDegrees) return false;
      // Verifica distanza per essere vicino all'anello
      const dist = Math.sqrt(x * x + y * y);
      const tolBase = this.strokeWidth * this.ringTolerance;
      const tol = tolBase + this.hitExtendPx; // estende finestra di hit
      if (dist < this.radius - tol || dist > this.radius + tol) return false;
      const frac = rel / this.arcDegrees;
      let val = this.min + frac * this.total;
      val = this.quantize(val);
      val = this.clamp(val);
      this.internalValue = parseFloat(val.toFixed(2));
      this.emit();
      return true;
    },
    polarToCartesian(angleDeg) {
      const rad = ((angleDeg - 90) * Math.PI) / 180;
      return {
        x: this.center + this.radius * Math.cos(rad),
        y: this.center + this.radius * Math.sin(rad),
      };
    },
    startDrag(e) {
      if (!this.setFromEvent(e)) {
        this.dragging = false;
        return;
      }
      this.dragging = true;
      window.addEventListener("mousemove", this.onMove);
      window.addEventListener("touchmove", this.onMove, { passive: false });
      window.addEventListener("mouseup", this.stopDrag);
      window.addEventListener("touchend", this.stopDrag);
    },
    onMove(e) {
      if (!this.dragging) return;
      e.preventDefault();
      this.setFromEvent(e);
    },
    stopDrag() {
      this.dragging = false;
      window.removeEventListener("mousemove", this.onMove);
      window.removeEventListener("touchmove", this.onMove);
      window.removeEventListener("mouseup", this.stopDrag);
      window.removeEventListener("touchend", this.stopDrag);
    },
    onKey(e) {
      let delta = 0;
      if (e.key === "ArrowUp" || e.key === "ArrowRight") delta = this.step;
      else if (e.key === "ArrowDown" || e.key === "ArrowLeft")
        delta = -this.step;
      if (delta !== 0) {
        this.internalValue = this.clamp(
          this.quantize(this.internalValue + delta)
        );
        this.emit();
      }
    },
    hexToRgb(hex) {
      const h = hex.replace("#", "");
      return {
        r: parseInt(h.substring(0, 2), 16),
        g: parseInt(h.substring(2, 4), 16),
        b: parseInt(h.substring(4, 6), 16),
      };
    },
  },
};
</script>

<style scoped>
.circular-slider {
  position: relative;
  display: inline-block;
  user-select: none;
  cursor: pointer;
}
.track {
  fill: none;
  stroke: #ddd;
  stroke-width: 18;
  stroke-linecap: round;
}
.progress {
  fill: none;
  stroke-width: 18;
  stroke-linecap: round;
  transition: stroke 0.2s linear;
}
.thumb {
  fill: #fff;
  stroke: #555;
  stroke-width: 3;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
}
.value-text {
  font-size: 45px;
  font-weight: 250;
  fill: #444;
  pointer-events: none;
  font-family: Roboto, Arial, sans-serif;
}
.circular-slider:focus {
  outline: none;
}
</style>
